import { useState, useEffect, useRef } from "react";
import { Bell, CheckCheck, MessageCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiCall, API, formatDate } from "@/lib/api";

const ICON_MAP = {
  globalwpp_complete: MessageCircle,
};

export const NotificationBell = ({ onNavigateCampaign }) => {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchNotifications = async () => {
    try {
      const { ok, data } = await apiCall("GET", `${API}/notifications`);
      if (ok) {
        setNotifications(data.notifications || []);
        setUnread(data.unread_count || 0);
      }
    } catch {}
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const markRead = async (id) => {
    await apiCall("PUT", `${API}/notifications/${id}/read`);
    setNotifications((prev) =>
      prev.map((n) => (n.notification_id === id ? { ...n, read: true } : n))
    );
    setUnread((u) => Math.max(0, u - 1));
  };

  const markAllRead = async () => {
    await apiCall("PUT", `${API}/notifications/read-all`);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n.notification_id);
    if (n.campaign_id && onNavigateCampaign) onNavigateCampaign(n.campaign_id);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(!open)}
        className="text-zinc-400 hover:text-white hover:bg-zinc-800 relative"
        data-testid="notification-bell"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-violet-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 overflow-hidden" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <span className="text-sm font-semibold text-white">Notificaciones</span>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs text-violet-400 hover:text-violet-300 h-7 px-2" data-testid="mark-all-read">
                  <CheckCheck className="w-3 h-3 mr-1" />Marcar leidas
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)} className="h-7 w-7 text-zinc-500 hover:text-white">
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-zinc-500 text-sm">
                Sin notificaciones
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = ICON_MAP[n.type] || Bell;
                return (
                  <button
                    key={n.notification_id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-zinc-800/50 transition-colors border-b border-zinc-800/50 ${!n.read ? "bg-violet-500/5" : ""}`}
                    data-testid={`notification-item-${n.notification_id}`}
                  >
                    <div className={`mt-0.5 w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${!n.read ? "bg-green-500/10 text-green-400" : "bg-zinc-800 text-zinc-500"}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-tight ${!n.read ? "text-white font-medium" : "text-zinc-400"}`}>
                        {n.title}
                      </p>
                      <p className="text-xs text-zinc-500 mt-0.5 truncate">{n.message}</p>
                      <p className="text-[10px] text-zinc-600 mt-1">{formatDate(n.created_at)}</p>
                    </div>
                    {!n.read && <div className="w-2 h-2 rounded-full bg-violet-500 mt-1.5 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
